import { cn } from "@/lib/cn";

type LogoProps = {
  size?: number;
  className?: string;
};

/**
 * Rentier 2050 brand mark — ascending R monogram with a coral accent dot
 * at the top right. The R gradient flows from bright amber into coral,
 * evoking growth / ascension.
 *
 * Used everywhere: sidebar, favicon, auth pages, emails.
 */
export function Logo({ size = 40, className }: LogoProps) {
  return (
    <div
      className={cn(
        "relative grid place-items-center rounded-xl border border-stroke-strong",
        "bg-gradient-to-br from-[#1f2430] to-[#141821]",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_6px_20px_-8px_rgba(245,178,99,0.3)]",
        className,
      )}
      style={{ width: size, height: size }}
      aria-label="Rentier 2050 logo"
    >
      <svg
        viewBox="0 0 32 32"
        width={size * 0.6}
        height={size * 0.6}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <defs>
          <linearGradient
            id="logo-grad-r"
            x1="0"
            y1="0"
            x2="32"
            y2="32"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0" stopColor="#ffc98a" />
            <stop offset="1" stopColor="#f5b263" />
          </linearGradient>
        </defs>
        {/* left stem */}
        <path d="M7 5 L7 27" stroke="url(#logo-grad-r)" strokeWidth="3" />
        {/* top bowl */}
        <path
          d="M7 5 L18 5 A6 6 0 0 1 18 17 L7 17"
          stroke="url(#logo-grad-r)"
          strokeWidth="3"
          fill="none"
        />
        {/* ascending leg */}
        <path d="M14 17 L25 27" stroke="url(#logo-grad-r)" strokeWidth="3" />
        {/* coral spark */}
        <circle cx="27" cy="6" r="2.4" fill="#ff7a59" />
      </svg>
    </div>
  );
}

type WordmarkProps = {
  className?: string;
};

export function Wordmark({ className }: WordmarkProps) {
  return (
    <div className={cn("font-serif leading-none", className)}>
      <div className="font-semibold text-[15.5px] tracking-tight">Rentier 2050</div>
      <div className="text-[9.5px] tracking-[0.22em] text-ink-dim uppercase font-sans font-medium mt-[3px]">
        Investor
      </div>
    </div>
  );
}

export function LogoLockup({
  size = 40,
  className,
}: {
  size?: number;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <Logo size={size} />
      <Wordmark />
    </div>
  );
}
